// BETA-CLEANUP: delete this file and its spec once Vue 3 is the only app (store.js goes back to plain cookie options)
import Cookies from 'js-cookie';

// This build runs as the opt-in beta next to the current production app (VITE_IS_BETA=true on
// the beta deploy only). Each app keeps its own host-only sign-in; switching between them hands the
// sign-in over through a short-lived cookie on VITE_COOKIE_DOMAIN, so the user lands signed in.
export const isBetaApp = import.meta.env.VITE_IS_BETA === 'true';

// The current production app the beta links back to, e.g. https://app.slickcactus.com
export const currentAppUrl = import.meta.env.VITE_CURRENT_APP_URL || '';

// `.slickcactus.com` on both deploys: the parent domain both apps read the sign-in handoff from; empty
// turns the handoff off (local development, staging)
export const sharedCookieDomain = import.meta.env.VITE_COOKIE_DOMAIN || undefined;

// vuex-persistedstate's default key, the cookie that holds the sign-in. It stays host-only on each app.
export const SIGN_IN_COOKIE = 'vuex';
export const SIGN_IN_COOKIE_EXPIRES_DAYS = 5;
export const signInCookieAttributes: Cookies.CookieAttributes = Object.freeze({
  secure: true,
  path: '/',
  sameSite: 'strict',
});

const SESSION_HANDOFF_COOKIE = 'sc-session-handoff';
// a minute: it only has to outlive the page load on the other app
const SESSION_HANDOFF_EXPIRES_DAYS = 1 / (24 * 60);

interface ISessionHandoff {
  to: string;
  signIn: string;
}

type PageLocation = Pick<Location, 'pathname' | 'search' | 'hash'>;

// Both apps share their routes, so the same path and query open the same page on the other one
// `stay` tells the current app not to send the user back to the beta on this load
export const getSamePageUrl = (appUrl: string, { pathname, search, hash }: PageLocation, stay = false): string => {
  const url = new URL(`${pathname}${search}${hash}`, appUrl);

  if (stay) url.searchParams.set('stay', '');

  return url.href;
};

const removeSessionHandoff = (): void => {
  Cookies.remove(SESSION_HANDOFF_COOKIE, { ...signInCookieAttributes, domain: sharedCookieDomain });
};

// Hands the sign-in to the other app for its next page load. Only this short-lived cookie is ever readable
// across the parent domain; the sign-in itself never leaves this host.
export const openOtherApp = (appUrl: string, stay = false): void => {
  const url = getSamePageUrl(appUrl, window.location, stay);
  const signIn = Cookies.get(SIGN_IN_COOKIE);

  if (sharedCookieDomain && signIn !== undefined) {
    const handoff: ISessionHandoff = { to: new URL(url).origin, signIn };

    Cookies.set(SESSION_HANDOFF_COOKIE, JSON.stringify(handoff), {
      ...signInCookieAttributes,
      domain: sharedCookieDomain,
      expires: SESSION_HANDOFF_EXPIRES_DAYS,
    });
  }

  window.location.assign(url);
};

// Only a page opened from the other app may hand a sign-in over: any host under the parent domain can write
// the cookie, but it cannot fake the referrer the browser sends on that navigation.
const isOpenedFromOtherApp = (): boolean => (
  !!currentAppUrl && !!document.referrer && new URL(document.referrer).origin === new URL(currentAppUrl).origin
);

// Runs before the persisted state reads the sign-in. A handoff addressed to another host (e.g. staging under
// the same parent domain) is left for that host.
export const receiveSessionHandoff = (): void => {
  const handoffCookie = Cookies.get(SESSION_HANDOFF_COOKIE);

  if (!sharedCookieDomain || handoffCookie === undefined) return;

  let handoff: ISessionHandoff;

  try {
    handoff = JSON.parse(handoffCookie);
  } catch {
    removeSessionHandoff();
    return;
  }

  // `?.`: valid JSON is not always an object (`null`, a number), and a throw here would stop the app from starting
  if (handoff?.to !== window.location.origin) return;

  removeSessionHandoff();

  if (typeof handoff.signIn !== 'string' || !isOpenedFromOtherApp()) return;

  Cookies.set(SIGN_IN_COOKIE, handoff.signIn, { ...signInCookieAttributes, expires: SIGN_IN_COOKIE_EXPIRES_DAYS });
};

// The preference lives on the user record; if saving it fails the current app is opened with `stay`
// and retries the save itself, so the user is not bounced back here.
export const leaveBeta = async (): Promise<void> => {
  let stay = false;

  try {
    // imported on use: the store imports this module, and the http layer imports the store
    const { authService } = await import('@/bundles/Auth/factory/authServiceFactory');

    await authService.setUseNewApp(false);
  } catch {
    stay = true;
  }

  openOtherApp(currentAppUrl, stay);
};
